import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme, alpha } from '@mui/material/styles'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'

type DataReuseStepProps = {
  checked: boolean
  onChange: (checked: boolean) => void
}

const dataReuseMessages = {
  title: msg`Health data reuse`,
  subtitle: msg`Before starting this teleconsultation, please review how your data is used.`,
  purpose1Title: msg`Teleconsultation improvement`,
  purpose1Description: msg`Using feedback to optimize digital care pathways and the fluidity of exchanges.`,
  purpose2Title: msg`Medical research`,
  purpose2Description: msg`Contribution to advanced clinical studies to identify new therapeutic protocols.`,
  purpose3Title: msg`Statistical analyses`,
  purpose3Description: msg`Epidemiological monitoring and public health resource management from consolidated data.`,
  instruction: msg`Please confirm that you have read the information regarding the processing of your data and express your consent below.`,
  consent:
    msg`I declare that I have been informed about the reuse of care data for purposes other than care, that I have understood this information, and I consent to it.`,
}

export function DataReuseStep({ checked, onChange }: DataReuseStepProps) {
  const { i18n } = useLingui()
  const theme = useTheme()

  const purposes = [
    {
      icon: <VideocamOutlinedIcon sx={{ color: theme.palette.primary.main }} />,
      title: i18n._(dataReuseMessages.purpose1Title),
      desc: i18n._(dataReuseMessages.purpose1Description),
    },
    {
      icon: <ScienceOutlinedIcon sx={{ color: theme.palette.primary.main }} />,
      title: i18n._(dataReuseMessages.purpose2Title),
      desc: i18n._(dataReuseMessages.purpose2Description),
    },
    {
      icon: <BarChartOutlinedIcon sx={{ color: theme.palette.primary.main }} />,
      title: i18n._(dataReuseMessages.purpose3Title),
      desc: i18n._(dataReuseMessages.purpose3Description),
    },
  ]

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {i18n._(dataReuseMessages.title)}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mt: 0.5 }}>
          {i18n._(dataReuseMessages.subtitle)}
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {purposes.map((purpose, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: 2, bgcolor: 'grey.50', borderRadius: '12px' }}>
            <Box sx={{ mt: 0.25 }}>{purpose.icon}</Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {purpose.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {purpose.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: '8px', borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.4)}` }}>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          {i18n._(dataReuseMessages.instruction)}
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            sx={{ color: theme.palette.grey[400], '&.Mui-checked': { color: theme.palette.primary.main }, '& .MuiSvgIcon-root': { fontSize: 24 } }}
          />
        }
        label={
          <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ lineHeight: 1.6 }}>
            {i18n._(dataReuseMessages.consent)}
          </Typography>
        }
        sx={{ alignItems: 'flex-start', mx: 0 }}
      />
    </Stack>
  )
}
